import { DEFECT_KINDS, DEFECT_PRIORITIES, DEFECT_STAGES } from '@felix/domain';
import { Button, Select, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendar,
  IconCircleCheck,
  IconClipboardList,
  IconClock,
  IconHourglass,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import table from '../materials/Materials.module.css';
import {
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { DefectLoadError, DefectPriorityBadge, DefectStageBadge } from './DefectParts.js';
import classes from './Defects.module.css';
import {
  type DefectColumn,
  type DefectFilter,
  defectKpis,
  equipmentLabel,
  filterDefects,
  NO_DEFECT_FILTER,
  sortDefects,
} from './routes.js';
import type { DefectsSource } from './source.js';
import type { DefectListResult } from './types.js';

const PAGE_SIZE = 10;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; error: unknown }
  | { kind: 'ready'; list: DefectListResult };

/**
 * Maintenance (PMS) ▸ Defects (ADR 0060 §11): başlık, bildirim aralığı, "New Defect" (gemi),
 * kartlar (Total, Open, Overdue, Pending Approval, Closed), arama ve süzgeçler, tablo (Defect No,
 * Reported, Vessel (ofis), Equipment / System, Description, Department, Priority, Stage, Target),
 * sıralama ve sayfalama. Gemide o gemi, ofiste filo.
 */
export function DefectList({
  source,
  onOpen,
  onNew,
}: {
  source: DefectsSource;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`defects.list.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<DefectFilter>(NO_DEFECT_FILTER);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<DefectColumn>>({ key: 'reported', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', list: await source.list() });
    } catch (error) {
      setState({ kind: 'error', error });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ship = source.scope === 'ship';
  const ready = state.kind === 'ready' ? state.list : null;
  const columns: DefectColumn[] = [
    'defectNo',
    'reported',
    ...(ship ? [] : (['vessel'] as const)),
    'equipment',
    'description',
    'department',
    'priority',
    'stage',
    'target',
  ];
  const items = useMemo(
    () => (ready ? sortDefects(filterDefects(ready.items, filter), sort.key, sort.dir) : []),
    [ready, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<DefectFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const kpis = ready ? defectKpis(ready.items) : null;
  const canCreate = Boolean(ship && source.editor && (ready?.can?.create ?? true));
  const tile = (
    key: 'total' | 'open' | 'overdue' | 'pending' | 'closed',
    status: DefectFilter['status'],
    icon: ReactNode,
    tone: 'default' | 'success' | 'warning' | 'danger',
  ) => (
    <button
      type="button"
      className={classes.tileButton}
      aria-pressed={filter.status === status && status !== null}
      onClick={() => change({ status: filter.status === status ? null : status })}
    >
      <StatTile icon={icon} label={k(`kpi.${key}`)} value={kpis ? kpis[key] : '—'} tone={tone} />
    </button>
  );

  return (
    <div className={classes.screen}>
      <PageHeader
        title={t('defects.title')}
        description={t('defects.description')}
        actions={
          <div className={classes.headerActions}>
            <fieldset className={classes.range} aria-label={k('dateRange')}>
              <IconCalendar size={16} aria-hidden />
              <DayInput
                variant="unstyled"
                size="xs"
                rightSection={null}
                aria-label={k('dateFrom')}
                placeholder={k('dateFrom')}
                value={filter.from}
                maxDate={filter.to ?? undefined}
                onChange={(value) => change({ from: value })}
              />
              <span aria-hidden>–</span>
              <DayInput
                variant="unstyled"
                size="xs"
                rightSection={null}
                aria-label={k('dateTo')}
                placeholder={k('dateTo')}
                value={filter.to}
                minDate={filter.from ?? undefined}
                onChange={(value) => change({ to: value })}
              />
            </fieldset>
            {canCreate ? (
              <Button leftSection={<IconPlus size={16} />} onClick={onNew}>
                {k('newDefect')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className={classes.tiles}>
        {tile('total', null, <IconClipboardList size={20} />, 'default')}
        {tile('open', 'open', <IconAlertTriangle size={20} />, 'danger')}
        {tile('overdue', 'overdue', <IconClock size={20} />, 'danger')}
        {tile('pending', 'pending', <IconHourglass size={20} />, 'warning')}
        {tile('closed', 'closed', <IconCircleCheck size={20} />, 'success')}
      </div>

      <Panel flush aria-label={k('table')}>
        <form
          className={classes.toolbar}
          aria-label={k('filters')}
          onSubmit={(event) => event.preventDefault()}
        >
          <TextInput
            label={k('search')}
            placeholder={k('searchPlaceholder')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={filter.search}
            onChange={(event) => change({ search: event.currentTarget.value })}
          />
          <Select
            label={k('status')}
            data={[
              { value: '', label: k('allStatus') },
              ...DEFECT_STAGES.map((value) => ({ value, label: t(`defects.stages.${value}`) })),
            ]}
            value={
              filter.status && (DEFECT_STAGES as readonly string[]).includes(filter.status)
                ? filter.status
                : ''
            }
            allowDeselect={false}
            onChange={(value) => change({ status: (value || null) as DefectFilter['status'] })}
          />
          <Select
            label={k('priority')}
            data={[
              { value: '', label: k('allPriority') },
              ...DEFECT_PRIORITIES.map((value) => ({
                value,
                label: t(`defects.priorities.${value}`),
              })),
            ]}
            value={filter.priority ?? ''}
            allowDeselect={false}
            onChange={(value) => change({ priority: value || null })}
          />
          <Select
            label={k('kind')}
            data={[
              { value: '', label: k('allKinds') },
              ...DEFECT_KINDS.map((value) => ({ value, label: t(`defects.kinds.${value}`) })),
            ]}
            value={filter.kind ?? ''}
            allowDeselect={false}
            onChange={(value) => change({ kind: value || null })}
          />
          {!ship ? (
            <Select
              label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={filter.vesselId ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ vesselId: value || null })}
            />
          ) : null}
        </form>
        {state.kind === 'error' ? (
          <DefectLoadError error={state.error} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={columns.length} label={k('table')} />
        ) : (ready?.items.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k(ship ? 'emptyShip' : 'emptyWeb')} />
        ) : items.length === 0 ? (
          <NoMatch title={k('noMatch')} onClear={() => change(NO_DEFECT_FILTER)} />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.nowrap}>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { no: item.defectNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.defectNo}
                        </a>
                      </td>
                      <td className={classes.nowrap}>{formatDay(item.localDate)}</td>
                      {ship ? null : <td>{item.vesselName ?? '—'}</td>}
                      <td>{equipmentLabel(item)}</td>
                      <td className={classes.wrap} title={item.description}>
                        {item.description}
                      </td>
                      <td>{item.department ? t(`defects.departments.${item.department}`) : '—'}</td>
                      <td>
                        <DefectPriorityBadge priority={item.priority} />
                      </td>
                      <td>
                        <span className={classes.badges}>
                          <DefectStageBadge stage={item.stage} awaitingParts={item.awaitingParts} />
                          {item.closure.status === 'pending' ? (
                            <span className={classes.muted}>
                              {/* Kimin onayı bekleniyor ve nerede (ADR 0091) */}
                              {item.closure.approverRoleName
                                ? t('defects.pendingApprovalRole', {
                                    role: item.closure.approverRoleName,
                                    where: t(
                                      item.closure.office
                                        ? 'defects.route.office'
                                        : 'defects.route.onBoard',
                                    ),
                                  })
                                : t('defects.pendingApproval')}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className={classes.target}>
                        {item.targetOn ? formatDay(item.targetOn) : '—'}
                        {item.overdue ? (
                          <span className={classes.overdueMark}>
                            <IconClock size={14} aria-hidden /> {t('defects.overdue')}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}
